import { Link } from "react-router-dom";
import { Bookmark, BookmarkCheck, Lock } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LOCKED_PATH_MSG } from "@/components/LockedPath";

export default function OpportunityCard({ opp, isSaved, onToggleSave, compareChecked, onToggleCompare, onFeedback }) {
  const [saved, setSaved] = useState(!!isSaved);
  const [busy, setBusy] = useState(false);

  const toggleSave = async () => {
    setBusy(true);
    try {
      if (saved) await api.delete(`/saved/${opp.id}`); else await api.post(`/saved/${opp.id}`);
      setSaved(!saved);
      onToggleSave?.(opp.id, !saved);
      toast.success(saved ? "Removed from saved" : "Saved");
    } catch { toast.error("Try again"); }
    setBusy(false);
  };

  return (
    <div className={`card-panel p-5 relative ${opp.locked ? "opacity-70" : ""}`} data-testid={`opp-card-${opp.id}`}>
      {opp.locked && (
        <div className="absolute inset-0 rounded-xl backdrop-blur-sm bg-black/40 flex flex-col items-center justify-center z-10">
          <Lock className="text-[#FD0F10]" />
          <div className="font-mono-ui text-xs uppercase tracking-wider mt-2 text-white">PRO Unlock</div>
          <p className="text-xs text-gray-200 mt-2 px-6 text-center max-w-xs" data-testid={`locked-msg-${opp.id}`}>{LOCKED_PATH_MSG}</p>
          <Link to="/pro" className="mt-3 hero-accent px-4 py-2 rounded text-sm font-bold" data-testid={`unlock-${opp.id}`}>Upgrade</Link>
        </div>
      )}
      <div className="flex items-start gap-3">
        <ScoreRing score={opp.escape_score} />
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-[#FD0F10]">ESCAPE SCORE · {opp.escape_score}%</div>
          <h3 className="font-display font-bold text-lg mt-1 leading-tight">{opp.title}</h3>
          <p className="text-sm text-gray-400 mt-1">{opp.tagline}</p>
          {opp.fit_reason && <p className="text-xs text-[#FD0F10] mt-1.5" data-testid={`fit-reason-${opp.id}`}>{opp.fit_reason}</p>}
        </div>
      </div>
      <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] sm:grid-cols-[repeat(4,minmax(0,1fr))] gap-2 mt-4 text-xs">
        <Meta label="COST" value={opp.startup_cost} />
        <Meta label="DIFFICULTY" value={opp.difficulty} />
        <Meta label="TIME" value={opp.time} />
        <Meta label="POTENTIAL" value={opp.income_potential} />
      </div>
      <p className="mt-2 text-[11px] text-gray-500" data-testid={`opp-earnings-disclaimer-${opp.id}`}>Results depend on your effort. The more hours you put in, the faster it usually grows. Most people earn less at first, and that's normal.</p>
      <div className="mt-4 flex items-center flex-wrap gap-2">
        <Link to={`/opportunity/${opp.id}`} className="hero-accent text-white px-4 py-2 rounded text-sm font-mono-ui uppercase tracking-wider" data-testid={`opp-open-${opp.id}`}>
          Open →
        </Link>
        <button onClick={toggleSave} disabled={busy} className="px-3 py-2 border border-[#2A2E39] rounded text-sm text-gray-300 hover:border-white flex items-center gap-1" data-testid={`opp-save-${opp.id}`}>
          {saved ? <BookmarkCheck size={16} className="text-[#FD0F10]"/> : <Bookmark size={16}/>}
          {saved ? "Saved" : "Save"}
        </button>
        {onToggleCompare && (
          <label className="text-xs font-mono-ui uppercase text-gray-400 flex items-center gap-1 ml-1">
            <input type="checkbox" checked={!!compareChecked} onChange={() => onToggleCompare(opp.id)} className="accent-[#FD0F10]" data-testid={`opp-compare-${opp.id}`} />
            Compare
          </label>
        )}
        <div className="flex-1"/>
        <div className="flex gap-1">
          {Object.entries(FB_LABELS).map(([a, [text, tip]]) => (
            <button key={a} onClick={() => onFeedback?.(opp.id, a)} title={tip} aria-label={tip} className="text-[10px] font-mono-ui uppercase tracking-wider px-2 py-1 border border-[#2A2E39] rounded text-gray-400 hover:border-[#FD0F10] hover:text-white" data-testid={`fb-${a}-${opp.id}`}>
              {text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const FB_LABELS = {
  like: ["👍 More like this", "Show me more paths like this one"],
  not_for_me: ["Not for me", "Hide this path"],
  too_expensive: ["Too pricey", "Hide this path, it costs too much"],
  too_difficult: ["Too hard", "Hide this path, it's too difficult"],
};

function Meta({ label, value }) {
  return (
    <div className="min-w-0 bg-[#0B0C0E] border border-[#2A2E39] rounded p-2">
      <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-xs text-gray-200 mt-0.5 break-words">{value}</div>
    </div>
  );
}

export function ScoreRing({ score, size = 56 }) {
  const pct = Math.max(3, Math.min(100, score));
  return (
    <div className="score-ring shrink-0" style={{ "--pct": `${pct}%`, width: size, height: size }}>
      <div className="w-full h-full rounded-full bg-[#0B0C0E] flex items-center justify-center">
        <span className="font-display font-black text-lg">{score}<span className="text-xs">%</span></span>
      </div>
    </div>
  );
}
